import React from 'react';
import PropTypes from 'prop-types';
import { SFEntityID, starfishMediaType, SFNotification } from 'utils/types';
import { sortSlots } from 'utils/scheduler';
import css from './Scheduler.css';
import StickyFooterLayout from 'common/StickyFooterLayout';
import StepSwitch from './StepSwitch';
import FilterControls from './FilterControls';
import Duration from './duration/Duration';
import Review from './review/Review';
import ConfirmationContainer from './confirmation/ConfirmationContainer';
import SlotsContainer from './slots/SlotsContainer';
import ConnectionReasonsContainer from './connection/ReasonsContainer';
import ServiceReasonsContainer from './service/ReasonsContainer';
import { assign, isEmpty } from 'lodash';
import CoursesView from './course/CoursesView';
import moment from 'moment';
import 'moment-timezone';
import {
  STEPS,
  getPreviousStep,
  getNextStep,
  getFirstStep,
  shouldDisplayBackButton, canClickOnContinueButton, backButtonText, continueButtonText, roundToNextFiveMinute
} from 'utils/scheduler';
import { getTenantConfig } from 'utils/tenant';
import { fetchAuthenticatedJSON, urlParams, apiErrorMessage } from 'utils/request';
import { getWord } from "utils/tenant";
import Footer from 'common/Footer';
import StyledButton from 'common/StyledButton';

export default class Scheduler extends React.Component {

constructor(props) {
    super(props);

  this.userTimeZone = isEmpty(props.userTimeZone) ? moment.tz.guess() : props.userTimeZone;
  this.daysRange = getTenantConfig("STUDENT_SCHEDULING_DEFAULT_DATE_RANGE") || 2;
  this.buttonBlurRef = React.createRef();
  
  this.state = {
      // Current step on the flow user is in
      step: getFirstStep(),

      // Data fetched from API
      appointmentTypes: null,
      appointmentReasons: null,
      courseOptions: null,
      recurringReason: null,
      slots: null,

      // Temporary user selections
      potentialDuration: null, // Duration user has selected, not necessarily saved yet

      // User inputs/selections for saving
      courseContextId: "",
      details: "",
      duration: null, // Current appointment duration selected by the user
      locationId: null, // id of the location we're saving
      reasonId: null,
      selectedSlot: null,

      // UI state management
      confirming: false,
      sectionInfoRetrievalInProgress: false,

      // ID for the saved appointment
      appointmentId: null,

      filterStartDate: roundToNextFiveMinute(moment().tz(this.userTimeZone)),
      filterEndDate: moment().tz(this.userTimeZone).add(this.daysRange, 'day').endOf('day')

    };

    this.onBackButtonClicked = this.onBackButtonClicked.bind(this);
    this.onContinueButtonClicked = this.onContinueButtonClicked.bind(this);
    this.onCancelButtonClicked = this.onCancelButtonClicked.bind(this);
  }

  onSlotSelected(selectedSlot) {
    this.setState({
      selectedSlot: selectedSlot,
      // set duration to the slot's minimum duration (can be changed by user later)
      duration: selectedSlot.minDurationMinutes,
      // potential duration to be saved as duration. Initializes as duration
      potentialDuration: selectedSlot.minDurationMinutes,
      // force user selection unless there's only one option
      locationId: this.locationIdFromSlot(selectedSlot),
      // if the slot contains a courseContext, set to slot's courseContextId
      courseContextId: selectedSlot.courseContextId,
      // if the slot contains a courseContext, it is the only course option
      courseOptions: selectedSlot.courseContext ? [selectedSlot.courseContext] : null
    });
  }

  // Returns a location ID for a slot if only one is possible, otherwise returns null.
  locationIdFromSlot(selectedSlot) {
    if (!selectedSlot) {
      return null;
    }
    return selectedSlot.locations.length > 1 ? null : selectedSlot.firstLocationId;
  }

  get selectedReasonName() {
    const { reasonId, appointmentReasons, appointmentTypes } = this.state;
    if ( reasonId === null ) return null;
    if ( appointmentReasons !== null ) {
      const selectedReason = appointmentReasons.find(reason => reason.id === parseInt(reasonId));
      return typeof selectedReason !== "undefined" ? selectedReason.name : null;
    }
    if ( appointmentTypes !== null ) {
      let reasonName = "";
      appointmentTypes.forEach(type => {
        const selectedReason = type.appointmentReasons.find(reason => reason.id === parseInt(reasonId));
        if ( typeof selectedReason !== "undefined" ) reasonName = selectedReason.name;
      });
      return reasonName !== "" ? reasonName : null;
    }
    // Should not get this far
     return null;

  }

  get selectedCourseName() {
    const { courseContextId, courseOptions } = this.state;
    if ( courseContextId === "" ) return null;
    if ( courseOptions !== null ) {
      const selectedCourse = courseOptions.find(course => course.id === parseInt(courseContextId));
      return typeof selectedCourse !== "undefined" ? selectedCourse.name : null;
    }
  }

  onBackButtonClicked() {
    if ( this.state.step === STEPS.CHANGE_DURATION ) this.setState({potentialDuration: this.state.duration});
    if ( this.state.step === STEPS.ADD_COURSE ) this.setState({courseContextId: ""});
    if ( this.state.step === STEPS.REVIEW_APPOINTMENT ) this.setState({locationId: this.locationIdFromSlot(this.state.selectedSlot)});
    this.setState({step: getPreviousStep(this.state.step)});
    this.props.notification.dismiss();
  }

  onCancelButtonClicked() {
      window.location.hash = "";
  }

  onSaveDurationClicked() {
    this.setState({duration: this.state.potentialDuration});
  }

  onContinueButtonClicked() {
    this.props.notification.dismiss();
    if ( this.state.step === STEPS.REVIEW_APPOINTMENT ) return this.onConfirmButtonClicked();
    else if ( this.state.step === STEPS.CHANGE_DURATION ) this.onSaveDurationClicked();
    else if ( this.state.step === STEPS.PICK_A_TIME && this.state.selectedSlot
      && this.state.selectedSlot.canChangeCourse && !this.state.sectionInfoRetrievalInProgress ) return this.fetchCourseContext().then(() => {
        if(this.buttonBlurRef.current) {
          this.buttonBlurRef.current.blur();
          sfNotificationCenter.fireEvent("moveFocusToHeader");
        }
      this.setState({step: getNextStep(this.state.step)})});
    this.setState({step: getNextStep(this.state.step)});
  }

  prepareData() {
    let URL = "";
    let form = {
      appointmentTypeReasonId: this.state.reasonId,
      description: this.state.details.trim(),
      duration: this.state.duration,
      locationId: this.state.locationId,
      courseContext: this.state.courseContextId};

    const startDate = !this.state.selectedSlot ? null : this.state.selectedSlot.slotStartDate.format("YYYY-MM-DD");
    const startTime = !this.state.selectedSlot ? null : this.state.selectedSlot.slotStartDate.format("HH:mm");

    if ( this.props.isConnection && this.state.recurringReason === false ) {
      // Call saveAppointment
      URL = "../appointments/saveAppointment.html";

      assign(form, {
        meetingType: "Appointment",
        appId: "",
        scheduleBlockId: !this.state.selectedSlot ? null : this.state.selectedSlot.scheduleBlockId,
        startDate: startDate,
        startTime: startTime,
        partyId: this.props.partyId
      });
    } else {
      // Call scheduleAppointment
      URL = "../appointments/scheduleAppointments.html";

      assign(form, {
        preview: false,
        servicePartyId: this.props.isService ? this.props.id : "",
        makerId: this.props.partyId,
        startDate: `${startDate} ${startTime}`,
        recurring: (this.state.recurringReason !== null && typeof this.state.recurringReason === 'boolean') ? this.state.recurringReason : false, 
        scheduleBlockType: !this.state.selectedSlot ? null : this.state.selectedSlot.scheduleBlockType,
        takerId: !this.state.selectedSlot ? null : this.state.selectedSlot.ownerId,
        requireCourseSpecificRelationship: this.props.isService && !isEmpty(this.props.restrictToSectionId)
      });
    }
    return { URL, form };
  }

  get isAppointmentValid() {
    return !(this.state.selectedSlot && this.state.selectedSlot.requireCourseContext && this.state.courseContextId === "");
  }

  // Calendar managers can see others' appointments in a given office hours block
  // when scheduling themselves via a service. It's important to return the ID of
  // their own appointment, otherwise you could get an error when calling the backend
  // for the appointment details.
  appointmentBelongingToUser(userId, appointments) {
    if (appointments.length === 1) {
      return appointments[0].appointmentId;
    }
    const apptBelongingToUser = appointments.find(appt => appt.personId === userId);
    return apptBelongingToUser ? apptBelongingToUser.appointmentId : appointments[0].appointmentId;
  }

  onConfirmButtonClicked() {
    if ( !this.isAppointmentValid ) {
      this.props.notification.error(`You must select a ${getWord('course', true)} for this appointment.`);
      return;
    }

    this.props.notification.dismiss(); // Dismiss existent notification

    this.setState({confirming: true});
    const { URL, form } = this.prepareData();
    return fetchAuthenticatedJSON(URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      body: urlParams(form)
    })
    .then(({appointmentId, result}) => {
      this.setState({
        step: getNextStep(this.state.step),
        appointmentId: appointmentId || this.appointmentBelongingToUser(sfUser.partyId, result.scheduledAppointments)
      });
      this.props.notification.success("I look forward to seeing you!"); // All good
    })
    .catch(this.onSaveError.bind(this))
    .then(() => this.setState({confirming: false}));
  }

  onSaveError() {
    this.props.notification.error("Sorry, the appointment is no longer available. Please try again.");
    this.setState({
      step: STEPS.PICK_A_TIME,
      courseOptions: null,
      slots: null,
      potentialDuration: null,
      courseContextId: "",
      details: "",
      duration: null,
      locationId: null,
      selectedSlot: null
    });
  }

  onReasonSelected(reasonId) {
    if ( this.state.reasonId && this.state.reasonId !== reasonId ) {
      this.setState({
        courseOptions: null,
        recurringReason: null,
        slots: null,
        courseContextId: "",
        potentialDuration: null,
        duration: null,
        selectedSlot: null,
        details: "",
        locationId: null
      });
    }

    if ( !this.props.isConnection ) {
      this.setState({reasonId});
      return;
    }

    const { appointmentTypes } = this.state;
    let isRecurring = false;

    if ( appointmentTypes && appointmentTypes.length > 0 ) {
      appointmentTypes.some(({recurring, appointmentReasons}) => {
        if ( recurring ) {
          const i = appointmentReasons.findIndex(({id}) => parseInt(id) === parseInt(reasonId));
          if ( i > -1 ) isRecurring = true;
        }
        return isRecurring;
      });
    }

    this.setState({reasonId, recurringReason: isRecurring});
  }

  onReasonsFetched(appointmentReasons, recurring) {
    this.setState({appointmentReasons, recurringReason: recurring});
  }

  onChangeReasonClicked() {
    // Goes back to beginning of the flow and reset all user selected data
    // apart from details
    this.props.notification.dismiss();
    this.setState({
      step: getFirstStep(),

      courseOptions: null,
      recurringReason: null,
      slots: null,

      potentialDuration: null,

      courseContextId: "",
      duration: null,
      locationId: null,
      reasonId: null,
      selectedSlot: null
    });
  }

  onChangeDurationClicked() {
    this.setState({step: STEPS.CHANGE_DURATION});
  }

  onDurationSelected(durationsMinutes) {
    this.setState({potentialDuration: parseInt(durationsMinutes)});
  }

  onCourseSelected(courseContextId) {
    this.setState({courseContextId: parseInt(courseContextId)});
  }

  onAddCourseClicked() {
    this.setState({step: STEPS.ADD_COURSE});
    this.props.notification.dismiss();
  }

  get locationName() {
    return !this.state.selectedSlot ? null
      : this.state.selectedSlot.findLocationName(this.state.locationId);
  }

  get locationInstructions() {
    return !this.state.selectedSlot ? null
      : this.state.selectedSlot.findLocationInstructions(this.state.locationId);
  }

  onLocationSelected(locationId) {
    this.setState({locationId: parseInt(locationId)});
  }

  onDetailsChanged(e) {
    this.setState({details: e.target.value});
  }

  fetchCourseContext() {
    if (!this.state.selectedSlot) return;
    this.setState({sectionInfoRetrievalInProgress: true});
    const URL = `../api/starfish/appointments/connections/${this.state.selectedSlot.ownerId}/sections?reasonUID=${this.state.reasonId}`;
    return fetchAuthenticatedJSON(URL, {
      method: 'GET',
      headers: {
        'Accept': starfishMediaType('appointments')
      }
    }).then(({sections}) => this.setState({
      courseOptions: sections,
      sectionInfoRetrievalInProgress: false
    }))
      .catch(e => {
        this.props.notification.error(apiErrorMessage(e));
        this.setState({sectionInfoRetrievalInProgress: false});
      });
  }

  renderConnectionReasonsContainer() {
    return (
      <ConnectionReasonsContainer
        id={this.props.id}
        connectionName={this.props.name}
        appointmentTypes={this.state.appointmentTypes}
        onAppointmentTypesFetched={appointmentTypes => this.setState({appointmentTypes})}
        value={this.state.reasonId}
        onReasonSelected={this.onReasonSelected.bind(this)}
        onFailure={this.props.notification.error}
      />
    );
  }

  renderServiceReasonsContainer() {
    return (
      <ServiceReasonsContainer
        id={this.props.id}
        serviceName={this.props.name}
        appointmentReasons={this.state.appointmentReasons}
        onReasonsFetched={this.onReasonsFetched.bind(this)}
        value={this.state.reasonId}
        onReasonSelected={this.onReasonSelected.bind(this)}
        onFailure={this.props.notification.error}
      />
    );
  }

  renderDuration() {
    return (
      <Duration
        durationsMinutes={!this.state.selectedSlot ? [] : this.state.selectedSlot.durationsMinutes}
        value={this.state.potentialDuration ? this.state.potentialDuration : this.state.duration}
        onDurationSelected={this.onDurationSelected.bind(this)}
      />
    );
  }

  renderCourse() {
    return (
      <CoursesView
        courseContextId={this.state.courseContextId}
        onCourseSelected={this.onCourseSelected.bind(this)}
        courseOptions={this.state.courseOptions}
      />
    );
  }

  renderSlotsContainer() {
    return (
      <FilterControls>
        <SlotsContainer
          type={this.props.isService ? "services" : "connections"}
          id={this.props.id}
          serviceCalendarInstruction={this.props.serviceCalendarInstruction}
          serviceCalendarEnabled={this.props.serviceCalendarEnabled}
          reasonId={this.state.reasonId}
          slots={this.state.slots}
          onSlotsFetched={(slots) => this.setState({slots: sortSlots(slots)})}
          ownerId={!this.state.selectedSlot ? null : this.state.selectedSlot.ownerId}
          startDate={!this.state.selectedSlot ? null : this.state.selectedSlot.slotStartDate}
          onSlotSelected={this.onSlotSelected.bind(this)}
          recurring={this.state.recurringReason}
          currentDuration={this.state.potentialDuration ? this.state.potentialDuration : this.state.duration}
          locationName={this.locationName}
          restrictToSectionId={this.props.restrictToSectionId}
          onFailure={this.props.notification.error}
          onFilterStartDateChanged={this.onFilterStartDateChanged.bind(this)}
          filterStartDate={this.state.filterStartDate}
          onFilterEndDateChanged={this.onFilterEndDateChanged.bind(this)}
          filterEndDate={this.state.filterEndDate}
        />
      </FilterControls>
    );
  }

  onFilterStartDateChanged(filterStartDate) {
    const filterStartOfDay = filterStartDate.clone().tz(this.userTimeZone).startOf('day');
    const newFilterStartDate = filterStartOfDay.isBefore() ?
      roundToNextFiveMinute(moment().tz(this.userTimeZone)) : filterStartOfDay;

    this.setState(Object.assign(
      {},
      { filterStartDate: newFilterStartDate },
      (newFilterStartDate.isAfter(this.state.filterEndDate)
        ? { filterEndDate: newFilterStartDate.clone().add(this.daysRange, 'days').endOf('day') }
        : {})));
  }

  onFilterEndDateChanged(filterEndDate) {
    this.setState({filterEndDate: filterEndDate.clone().tz(this.userTimeZone).endOf('day')});
  }

  renderReview() {
    return (
      <Review
        startDate={!this.state.selectedSlot ? null : this.state.selectedSlot.slotStartDate}
        duration={this.state.duration}
        locationId={this.state.locationId}
        locationName={this.locationName}
        locations={!this.state.selectedSlot ? null : this.state.selectedSlot.locations}
        participants={!this.state.selectedSlot ? null : this.state.selectedSlot.groupSessionParticipants}
        instructions={this.locationInstructions}
        reasonName={this.selectedReasonName}
        courseName={this.selectedCourseName}
        recurring={this.state.recurringReason}
        recurrenceLabel={!this.state.selectedSlot ? null : this.state.selectedSlot.slotTimeRangeWithRecurrence}
        onChangeReasonClicked={this.onChangeReasonClicked.bind(this)}
        details={this.state.details}
        onDetailsChanged={this.onDetailsChanged.bind(this)}
        canChangeDuration={!this.state.selectedSlot ? false : this.state.selectedSlot.canChangeDuration}
        onChangeDurationClicked={this.onChangeDurationClicked.bind(this)}
        canChangeLocation
        onLocationSelected={this.onLocationSelected.bind(this)}
        canChangeCourse={!this.state.selectedSlot ? false : this.state.selectedSlot.canChangeCourse}
        onAddCourseClicked={this.onAddCourseClicked.bind(this)}
        displayTeamMember={this.props.isService ? true : false}
        ownerName={!this.state.selectedSlot ? null : this.state.selectedSlot.ownerName}
        ownerTitle={!this.state.selectedSlot ? null : this.state.selectedSlot.ownerTitle}
        ownerPhotoURL={!this.state.selectedSlot ? null : this.state.selectedSlot.ownerPhotoURL}
        scheduleBlockInstructions={!this.state.selectedSlot ? null : this.state.selectedSlot.scheduleBlockInstructions}
        courseOptions={this.state.courseOptions}
        requireCourseContext={!this.state.selectedSlot ? false : this.state.selectedSlot.requireCourseContext}
      />
    );
  }

  renderConfirmation() {
    return (
      <ConfirmationContainer
        id={this.state.appointmentId}
        displayTeamMember={this.props.isService ? true : false}
        ownerPhotoURL={!this.state.selectedSlot ? null : this.state.selectedSlot.ownerPhotoURL}
        studentTimeZone={!this.state.selectedSlot.studentTimeZone ? null : this.state.selectedSlot.studentTimeZone}
        onFailure={this.props.notification.error}
      />
    );
  }

  get footer() {
    const { step, reasonId, confirming, courseContextId, locationId, sectionInfoRetrievalInProgress } = this.state;
    if ( step === STEPS.CONFIRMATION_SCREEN ) return null;
    const startDate = !this.state.selectedSlot ? null : this.state.selectedSlot.slotStartDate;
    const ownerId = !this.state.selectedSlot ? null : this.state.selectedSlot.ownerId;
    return (
      <Footer
        primaryButton={
          <StyledButton 
            ref={this.buttonBlurRef}
            disabled={!canClickOnContinueButton(step, {reasonId, startDate, ownerId, confirming, courseContextId, locationId, sectionInfoRetrievalInProgress})}
            onClick={this.onContinueButtonClicked}>
            {continueButtonText(step, confirming, sectionInfoRetrievalInProgress)}
          </StyledButton>
        }
        secondaryButton={
          <StyledButton
            asLink
            onClick={shouldDisplayBackButton(step) ? this.onBackButtonClicked : this.onCancelButtonClicked}>
            {shouldDisplayBackButton(step) ? backButtonText(step) : "Cancel"}
          </StyledButton>
        }
      />
    )
  }

  render() {
    return (
      <StickyFooterLayout
        header={this.props.notification.render()}
        content={
          <div className={css['content']}>
            {this.props.header}
            <StepSwitch
              step={this.state.step}
              isService={this.props.isService}
              isConnection={this.props.isConnection}
              renderConnectionReasonsContainer={this.renderConnectionReasonsContainer.bind(this)}
              renderServiceReasonsContainer={this.renderServiceReasonsContainer.bind(this)}
              renderSlotsContainer={this.renderSlotsContainer.bind(this)}
              renderReview={this.renderReview.bind(this)}
              renderDuration={this.renderDuration.bind(this)}
              renderConfirmation={this.renderConfirmation.bind(this)}
              renderCourse={this.renderCourse.bind(this)}
            />
          </div>
        }
        footer={this.footer}
      />
    );
  }
}

Scheduler.propTypes = {
  id: SFEntityID.isRequired,
  header: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  isConnection: PropTypes.bool,
  isService: PropTypes.bool,
  serviceCalendarInstruction: PropTypes.string,
  serviceCalendarEnabled: PropTypes.bool,
  partyId: SFEntityID.isRequired,
  restrictToSectionId: SFEntityID,
  notification: SFNotification.isRequired,
  userTimeZone: PropTypes.string.isRequired
};

Scheduler.defaultProps = {
  isService: false
};
